import {
	createContext,
	PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import type {
	UseWizardStepOptions,
	WizardSave,
	WizardSaveResult,
	WizardStepHandle,
	WizardStepRegistry,
} from './WizardStepRegistry.types';

export const SAVE_OK: WizardSaveResult = { ok: true };
export const SAVE_SKIPPED: WizardSaveResult = { ok: true, skipped: true };
export const SAVE_INVALID: WizardSaveResult = { ok: false, reason: 'invalid' };
export const SAVE_FAILED: WizardSaveResult = { ok: false, reason: 'request-failed' };

/** For steps that have nothing to persist: registers so the shell knows the step is present. */
export const noSave: WizardSave = () => Promise.resolve(SAVE_SKIPPED);
/** For steps that persist as the user acts, such as uploads: nothing is left to save, but nothing was skipped either. */
export const nothingToSave: WizardSave = () => Promise.resolve(SAVE_OK);

export const useWizardStepRegistry = (): WizardStepRegistry => {
	const activeRef = useRef<WizardStepHandle | null>(null);
	const [activeStepPersists, setActiveStepPersists] = useState(false);

	const register = useCallback((handle: WizardStepHandle) => {
		if (activeRef.current) {
			throw new Error(
				'A wizard step is already registered; only the active step may register'
			);
		}
		activeRef.current = handle;
		setActiveStepPersists(handle.persists);
		return () => {
			if (activeRef.current === handle) {
				activeRef.current = null;
				setActiveStepPersists(false);
			}
		};
	}, []);

	const saveActiveStep = useCallback<WizardSave>((intent) => {
		const active = activeRef.current;
		if (!active) {
			return Promise.reject(new Error(`No wizard step is registered to handle "${intent}"`));
		}
		return active.save(intent);
	}, []);

	const hasActiveStep = useCallback(() => activeRef.current !== null, []);

	return useMemo(
		() => ({ register, saveActiveStep, hasActiveStep, activeStepPersists }),
		[register, saveActiveStep, hasActiveStep, activeStepPersists]
	);
};

const WizardStepRegistryContext = createContext<WizardStepRegistry | null>(null);

interface Props {
	registry: WizardStepRegistry;
}

export const WizardStepRegistryProvider = ({ registry, children }: PropsWithChildren<Props>) => (
	<WizardStepRegistryContext.Provider value={registry}>
		{children}
	</WizardStepRegistryContext.Provider>
);

/** Registers the calling step with the surrounding wizard for as long as it is mounted. */
export const useWizardStep = (save: WizardSave, { persists = true }: UseWizardStepOptions = {}) => {
	const registry = useContext(WizardStepRegistryContext);
	if (!registry) {
		throw new Error('useWizardStep must be used inside WizardStepRegistryProvider');
	}
	const { register } = registry;

	const saveRef = useRef(save);
	useEffect(() => {
		saveRef.current = save;
	}, [save]);

	useLayoutEffect(
		() => register({ save: (intent) => saveRef.current(intent), persists }),
		[register, persists]
	);
};

/** Wraps a step that has nothing to persist so it still registers with the wizard. */
export const NoSaveStep = ({ children }: PropsWithChildren) => {
	useWizardStep(noSave, { persists: false });
	return <>{children}</>;
};
